/**
 * What a member of a merged document will OUTPUT, what is VISIBLE on its
 * canvas, and what governed a burn (VE-30104).
 *
 * The two answers differ only while the watermark panel is open: output
 * ignores the preview, visible follows it. Everything that leaves the editor
 * reads output; only the page reads visible. Collapsing them is how the rail
 * badge came to report a preview as applied (VE-31894).
 *
 * Members store nothing: set-wide marks are resolved on every render from the
 * project plus the member's own membership, which is why they follow the
 * reviewer to the next document untouched and why a member added later
 * inherits them. A member can also carry marks of its OWN, so the page draws
 * the union, minus its copy of anything the set already carries.
 *
 * Simpler than its sibling `renderedBates.ts`: a watermark is IDENTICAL on
 * every member, so there is no position arithmetic and a reorder invalidates
 * nothing.
 */
import { createSelector } from '@reduxjs/toolkit';

import { selectEditorRequestFolder } from '@redact-modules/casemedia/selectors';
import {
  selectVisibleWatermarks,
  selectCurrentTdoId,
  selectCommittedWatermarks,
  selectWatermarkPreview,
  selectWatermarksOff,
} from '@redact-modules/documents/selectors';
import type { AppliedWatermark } from '@redact-modules/documents/models';
import type { TDOId } from '@common-modules/universal/models/Brands';

import {
  projectOfFolder,
  selectProjectMembers,
  selectProjectRedaction,
} from './selectors';
const EMPTY: readonly AppliedWatermark[] = [];

import {
  mergeSetAndOwnWatermarks,
  resolveWatermarkGovernance,
  UNGOVERNED,
  watermarksToDraw,
  type WatermarkGovernance,
} from './projectRedactionAsset';

type WatermarkRenderState = Parameters<typeof selectVisibleWatermarks>[0] &
  Parameters<typeof selectEditorRequestFolder>[0] &
  Parameters<ReturnType<typeof selectProjectRedaction>>[0];

/**
 * The project whose settings govern the open request, or undefined.
 *
 * SCOPED TO THE OPEN REQUEST, not the `tdoId` asked about — membership comes
 * from the request's project and nothing indexes TDO to project. A caller
 * that outlives the editor (a burn-in) must read this AT THE CLICK: later it
 * is undefined, because leaving for the folder view clears the request.
 */
const selectGoverningProjectId = (state: WatermarkRenderState) =>
  projectOfFolder(selectEditorRequestFolder(state))?.tdoId;

/**
 * The project whose marking this file belongs to, or undefined.
 *
 * SEPARATE FROM `selectWatermarkGovernanceFor`, which answers "what should
 * this file draw" and is `ungoverned` for a set with no marks yet. The raster
 * upload asks the other question — where a set-wide mark's artwork belongs —
 * and that is the project from the first mark on, including the one being
 * added now.
 *
 * Undefined until membership is read: the loader fails closed on a container
 * mismatch, so a raster written to the wrong one is not recoverable.
 */
export const selectWatermarkProjectFor =
  (tdoId: TDOId | string | undefined) =>
  (state: WatermarkRenderState): TDOId | undefined => {
    const projectTdoId = selectGoverningProjectId(state);
    if (!projectTdoId || !tdoId) {
      return undefined;
    }
    const members = selectProjectMembers(projectTdoId)(state);
    return members?.some((id) => String(id) === String(tdoId))
      ? projectTdoId
      : undefined;
  };

/**
 * MEMOISED PER `tdoId`, and returning shared constants for the stateless
 * answers.
 *
 * `DocumentEditorView` subscribes with `useSelector`, which compares by
 * reference — so a fresh `{status:'ungoverned'}` per call re-renders the
 * editor on every action, for every document, member or not. It also feeds
 * the `createSelector` below, so that memo never hits either.
 *
 * `selectBatesGovernanceFor` caches the same way, for the same reason.
 */
const governanceCache = new WeakMap<object, Map<string, WatermarkGovernance>>();

export const selectWatermarkGovernanceFor =
  (tdoId: TDOId | string | undefined) =>
  (state: WatermarkRenderState): WatermarkGovernance => {
    const projectTdoId = selectGoverningProjectId(state);
    if (!projectTdoId || !tdoId) {
      return UNGOVERNED;
    }
    const record = selectProjectRedaction(projectTdoId)(state);
    const members = selectProjectMembers(projectTdoId)(state);
    const next = resolveWatermarkGovernance({ record, members, tdoId });
    // Keyed on the RECORD object, which changes identity exactly when the
    // store slice does, so a cached answer can never outlive its inputs.
    let byTdo = governanceCache.get(record);
    if (!byTdo) {
      byTdo = new Map();
      governanceCache.set(record, byTdo);
    }
    const key = `${String(projectTdoId)}|${String(tdoId)}|${
      members ? members.length : -1
    }`;
    const cached = byTdo.get(key);
    if (
      cached &&
      cached.status === next.status &&
      (cached.status !== 'governed' ||
        (next.status === 'governed' &&
          cached.marks === next.marks &&
          cached.modifiedDateTime === next.modifiedDateTime))
    ) {
      return cached;
    }
    byTdo.set(key, next);
    return next;
  };

/** Governance of the document on screen. */
const selectCurrentWatermarkGovernance = (state: WatermarkRenderState) =>
  selectWatermarkGovernanceFor(selectCurrentTdoId(state) ?? undefined)(state);

/**
 * WHAT THE REDACTED FILE WILL CARRY — never the panel's preview (VE-31894).
 *
 * THE UNION of the set's marks and this file's own, since scope is per mark.
 * The combining rule lives in `watermarksToDraw`, shared with the burn, which
 * is what makes this the output's answer rather than a second opinion about
 * it. Every state but `governed` and `suppressed` gives the file's own marks
 * and nothing from the set — they differ for STALENESS, not for drawing, and
 * `watermarkStalenessVerdict` owns that.
 *
 * A member whose record has not resolved carries none of the SET's marks —
 * guessing is how a document shows a mark the set never had with every check
 * reporting it current — but it does carry its own.
 *
 * OUTPUT, NOT "APPLIED", and the distinction is load-bearing: `watermarksToDraw`
 * returns nothing for an ungoverned file whose `watermarksOff` switch is on,
 * so marks that ARE applied are absent here. They are absent from the burn for
 * the same reason, which is the question the rail's badge is really asking.
 *
 * Drives that badge. Reading the VISIBLE marks instead lit it for a mark added
 * but not applied, and cleared it while the panel's switch was off.
 */
export const selectOutputMergedWatermarks = createSelector(
  [
    selectCommittedWatermarks,
    selectWatermarksOff,
    selectCurrentWatermarkGovernance,
  ],
  (ownMarks, ownOff, governance): readonly AppliedWatermark[] =>
    watermarksToDraw({ governance, ownMarks, ownWatermarksOff: ownOff })
);

/**
 * The badge's answer as a boolean, so a rebuilt list with the same answer does
 * not re-render the editor.
 */
export const selectHasOutputMergedWatermarks = (
  state: WatermarkRenderState
): boolean => selectOutputMergedWatermarks(state).length > 0;

/**
 * WHAT THE CANVAS SHOWS: the output marks above, unless the panel's live
 * preview is open — in which case it wins, applied or not.
 */
export const selectVisibleMergedWatermarks = createSelector(
  [
    selectVisibleWatermarks,
    selectWatermarkPreview,
    selectOutputMergedWatermarks,
  ],
  (visible, preview, output): readonly AppliedWatermark[] => {
    // THE PANEL'S LIVE PREVIEW WINS, governed or not. For a member it holds
    // BOTH lists, so while the panel is open the page follows what the
    // reviewer is doing rather than the two records they are replacing.
    // `visible` already resolves a preview to its marks, so this is only the
    // decision that it comes first.
    if (preview) {
      // SPLIT BACK INTO TWO LISTS, because the preview holds them merged and
      // the set's have to draw underneath. The duplicate is suppressed by the
      // same rule the committed path uses rather than deduped out of the
      // seed: a mark missing from the preview is missing from the list Apply
      // commits, which would delete it.
      if (preview.setWideIds === undefined) {
        return visible;
      }
      const setIds = new Set(preview.setWideIds);
      const setMarks = preview.marks.filter((w) => setIds.has(w.instanceId));
      const ownInPreview = preview.marks.filter(
        (w) => !setIds.has(w.instanceId)
      );
      // ONE SWITCH, ONE ANSWER — off draws nothing at all, the file's own
      // marks included. `mergeSetAndOwnWatermarks` returns the own list
      // unchanged when the set has none, so there is no second case here.
      return preview.enabled
        ? mergeSetAndOwnWatermarks(setMarks, ownInPreview)
        : EMPTY;
    }
    return output;
  }
);
